import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '../../services/api';
import { fetchRoles } from '../../services/roleService';
import { AccessLevel, User } from '../../types';

interface AuthState {
  user:         User | null;
  token:        string | null;
  activeBranch: string | null;
  loading:      boolean;
  error:        string | null;
  /** This user's role's module access map (Settings > Roles & Permissions) — null until fetched. */
  roleAccess:   Record<string, AccessLevel> | null;
}

const initialState: AuthState = {
  user:         null,
  token:        localStorage.getItem('token'),
  activeBranch: localStorage.getItem('branchId'),
  loading:      false,
  error:        null,
  roleAccess:   null,
};

export const login = createAsyncThunk(
  'auth/login',
  async (credentials: { email: string; password: string }, { rejectWithValue }) => {
    try {
      const { data } = await api.post('/auth/login', credentials);
      localStorage.setItem('token', data.token);
      if (data.data?.defaultBranch) localStorage.setItem('branchId', data.data.defaultBranch);
      return data;
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      return rejectWithValue(e.response?.data?.message || 'Login failed');
    }
  },
);

// Resolves the caller's own role into a module access map so the Sidebar and
// route guards can hide/block modules the role has no access to — the API
// itself already enforces this per-request; this just keeps the UI honest.
export const fetchRoleAccess = createAsyncThunk(
  'auth/fetchRoleAccess',
  async (role: string, { rejectWithValue }) => {
    try {
      const { data } = await fetchRoles();
      return data.data.find(r => r.key === role)?.access ?? {};
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      return rejectWithValue(e.response?.data?.message || 'Could not load permissions');
    }
  },
);

export const logout = createAsyncThunk('auth/logout', async () => {
  localStorage.removeItem('token');
  localStorage.removeItem('branchId');
});

// Rehydrates `user` from the still-valid token after a page refresh (token
// itself survives in localStorage, but the profile/role only ever lived in
// Redux memory, so a refresh otherwise leaves `user` null indefinitely).
export const fetchCurrentUser = createAsyncThunk(
  'auth/fetchCurrentUser',
  async (_: void, { rejectWithValue }) => {
    try {
      const { data } = await api.get('/auth/me');
      return data;
    } catch (err: unknown) {
      const e = err as { response?: { status?: number; data?: { message?: string } } };
      return rejectWithValue({
        status:  e.response?.status,
        message: e.response?.data?.message || 'Session expired',
      });
    }
  },
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setActiveBranch: (state, action: PayloadAction<string>) => {
      state.activeBranch = action.payload;
      localStorage.setItem('branchId', action.payload);
    },
    clearError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending,   (s) => { s.loading = true; s.error = null; })
      .addCase(login.fulfilled, (s, a) => {
        s.loading = false;
        s.token = a.payload.token;
        s.user = a.payload.data;
        s.activeBranch = a.payload.data?.defaultBranch ?? null;
      })
      .addCase(login.rejected,  (s, a) => { s.loading = false; s.error = a.payload as string; })
      .addCase(logout.fulfilled,(s) => { s.user = null; s.token = null; s.activeBranch = null; s.roleAccess = null; })
      .addCase(fetchCurrentUser.fulfilled, (s, a) => {
        s.user = a.payload.data;
        if (!s.activeBranch && a.payload.data?.defaultBranch) s.activeBranch = a.payload.data.defaultBranch;
      })
      .addCase(fetchCurrentUser.rejected, (s, a) => {
        // Only an actual auth failure (invalid/expired token) should log the
        // user out. A transient failure — rate limiting, a 5xx, a network
        // blip — must not silently destroy an otherwise-valid session; the
        // token stays put and the next navigation gets to retry.
        const status = (a.payload as { status?: number } | undefined)?.status;
        if (status === 401) { s.user = null; s.token = null; s.activeBranch = null; }
      })
      .addCase(fetchRoleAccess.fulfilled, (s, a) => { s.roleAccess = a.payload as Record<string, AccessLevel>; });
  },
});

export const { setActiveBranch, clearError } = authSlice.actions;
export default authSlice.reducer;
